import { pick } from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';

import { createOffer } from '../../mutations';
import {
  StoreProvider,
  Label,
  ErrorFlash,
  ImageUploader,
} from '../../components';
import { HOURS, START_OF_DAY, END_OF_DAY } from '../constants';
import 'react-datepicker/dist/react-datepicker.css';
import DatePresenter from '../../presenters/DatePresenter';

export class OfferNewView extends Component {
  constructor(props) {
    super(props);
    const offer = props?.location?.state?.offer || null;
    const store = offer?.store || null;

    this.state = {
      isDuplicate: !!offer || false,
      errors: null,
      title: offer?.title ? `${offer?.title} copy` : '',
      description: offer?.description || '',
      finePrint: offer?.finePrint || '',
      storeId:
        store?.id ||
        props.brandId ||
        props?.match?.params?.brandId ||
        'no-selection',
      imageUrl: offer?.imageUrl || null,
      backgroundImage: null,
      startDate:
        moment(offer?.startDate).startOf('day') || moment().startOf('day'),
      startTime: moment(offer?.startDate).hour(),
      endDate: moment(offer?.endDate).startOf('day') || moment().startOf('day'),
      endTime: moment(offer?.endDate).hour(),
      isFlash: offer?.isFlash || false,
      isPromoted: offer?.isPromoted || false,
      isLatest: offer?.isLatest || false,
      isRedeemable: offer?.isRedeemable || false,
      isTop: offer?.isTop || false,
      isCheckIn: offer?.isCheckIn || false,
      redemptionCode: offer?.redemptionCode || '',
      isLoading: false,
    };
  }

  async componentDidMount() {
    const offer = this.props?.location?.state?.offer || null;
    if (offer?.imageUrl) {
      this.setState({ isLoading: true });
      const file = await this.getFileFromUrl(offer?.imageUrl);
      this.setState({ backgroundImage: file, isLoading: false });
    }
  }

  getFileFromUrl = async (url, name, defaultType = 'image/jpeg') => {
    const response = await fetch(url);
    const data = await response.blob();
    const file = await new File([data], name, {
      type: data.type || defaultType,
    });
    return file;
  };

  notify = () =>
    toast.success('Offer Successfully Created!', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

  handleSubmit = async (event) => {
    this.setState({ isLoading: true });
    event.preventDefault();
    const { lists, relay } = this.props;
    const attributes = pick(this.state, [
      'title',
      'description',
      'finePrint',
      'storeId',
      'backgroundImage',
      'isFlash',
      'isPromoted',
      'isRedeemable',
      'isLatest',
      'isTop',
      'isCheckIn',
      'redemptionCode',
    ]);

    const { startDate, startTime, endDate, endTime } = this.state;
    attributes.startDate = moment(startDate)
      .hour(startTime)
      .minutes(0)
      .toISOString();
    attributes.endDate = moment(endDate).hour(endTime).minutes(0).toISOString();

    createOffer(relay.environment, attributes, lists.id, {
      onCompleted: this.handleSubmitSuccess,
    });
  };

  handleSubmitSuccess = (response) => {
    this.setState({ isLoading: false });
    const { errors } = response.createOffer;
    const isFromBrand = this.props?.location?.state?.isFromBrand || false;
    if (errors) {
      this.setState({ errors });
    } else {
      this.props?.match?.params?.brandId || isFromBrand
        ? this.props.history.push(
            `/brands/${response.createOffer.offerEdge.node.store.id}`
          )
        : this.props.history.push(`/offers/tab/active`);
      this.notify();
    }
  };

  handleStore = (event) => {
    this.setState({ storeId: event.target.value });
  };

  handleTitle = (event) => {
    this.setState({ title: event.target.value });
  };

  handleDescription = (event) => {
    this.setState({ description: event.target.value });
  };

  handleImageUploaded = (image) => {
    this.setState({ backgroundImage: image });
  };

  handleStartDate = (date) => {
    if (date !== null) {
      this.setState({ startDate: date });
    }
  };

  handleStartTime = (event) => {
    const hour = event.target.value;
    this.setState({ startTime: hour });
  };

  handleEndDate = (date) => {
    if (date !== null) {
      this.setState({ endDate: date });
    }
  };

  handleEndTime = (event) => {
    const hour = event.target.value;
    this.setState({ endTime: hour });
  };

  handleIsFlash = (event) => {
    this.setState({ isFlash: event.target.checked });
  };

  handleIsPromoted = (event) => {
    this.setState({ isPromoted: event.target.checked });
  };

  handleIsLatest = (event) => {
    this.setState({ isLatest: event.target.checked });
  };

  handleIsCheckIn = (event) => {
    this.setState({ isCheckIn: event.target.checked });
  };

  handleIsRedeemable = (event) => {
    this.setState({ isRedeemable: event.target.checked });
  };

  handleIsTop = (event) => {
    this.setState({ isTop: event.target.checked });
  };

  handleRedemptionCode = (event) => {
    this.setState({ redemptionCode: event.target.value });
  };

  handleFinePrint = (event) => {
    this.setState({ finePrint: event.target.value });
  };

  render() {
    const { errors, isDuplicate, isLoading } = this.state;
    const stores = this.props.lists.stores.edges.map((e) => e.node);

    if (isLoading) {
      return (
        <div className="loading_container">
          <div className="lds-dual-ring"></div>
        </div>
      );
    }

    return (
      <div className="offer-new">
        <form className="offer-form" onSubmit={this.handleSubmit}>
          <div className="form-header">
            <h2>{isDuplicate ? 'Duplicate' : 'New'} Offer</h2>
            <div className="form-header-actions">
              <input
                id="saveOffer"
                className="offer-edit-submit"
                type="submit"
                value="Save"
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form-fields">
            <ErrorFlash errors={errors} />
            <div className="form-group">
              <h3>General</h3>
              <div className="form-group-fields">
                <div className="form-group-column">
                  <div className="store-field">
                    <Label attr="store" text="Store" errors={errors} />
                    <select
                      id="store"
                      value={this.state.storeId}
                      onChange={this.handleStore}
                    >
                      <option disabled="disabled" value="no-selection">
                        {' '}
                        -- select a store --{' '}
                      </option>
                      {stores.map((store, i) => (
                        <option value={store.id} key={i}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="title-field">
                    <Label attr="title" text="Title" errors={errors} />
                    <input
                      type="text"
                      id="title"
                      value={this.state.title}
                      onChange={this.handleTitle}
                      required
                    />
                  </div>

                  <div className="description-field">
                    <Label
                      attr="description"
                      text="Description"
                      errors={errors}
                    />
                    <textarea
                      id="description"
                      value={this.state.description}
                      onChange={this.handleDescription}
                    />
                  </div>

                  <div className="fine-print-field">
                    <Label attr="finePrint" text="Fine Print" errors={errors} />
                    <textarea
                      id="finePrint"
                      value={this.state.finePrint}
                      onChange={this.handleFinePrint}
                    />
                  </div>

                  <div className="is-flash-field checkbox">
                    <input
                      type="checkbox"
                      id="isFlash"
                      checked={this.state.isFlash}
                      onChange={this.handleIsFlash}
                    />
                    <Label attr="isFlash" text="Is Flash" errors={errors} />
                  </div>

                  <div className="is-promoted-field checkbox">
                    <input
                      type="checkbox"
                      id="isPromoted"
                      checked={this.state.isPromoted}
                      onChange={this.handleIsPromoted}
                    />
                    <Label
                      attr="isPromoted"
                      text="Is Promoted"
                      errors={errors}
                    />
                  </div>

                  <div className="is-top-field checkbox">
                    <input
                      type="checkbox"
                      id="isTop"
                      checked={this.state.isTop}
                      onChange={this.handleIsTop}
                    />
                    <Label attr="isTop" text="Is A Top Offer" errors={errors} />
                  </div>

                  <div className="is-latest-field checkbox">
                    <input
                      type="checkbox"
                      id="isLatest"
                      checked={this.state.isLatest}
                      onChange={this.handleIsLatest}
                    />
                    <Label
                      attr="isLatest"
                      text="Is A Latest Offer"
                      errors={errors}
                    />
                  </div>
                  <div className="is-check-in-field checkbox">
                    <input
                      type="checkbox"
                      id="isCheckIn"
                      checked={this.state.isCheckIn}
                      onChange={this.handleIsCheckIn}
                    />
                    <Label
                      attr="isCheckIn"
                      text="Is Check-In Offer"
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="form-group-column">
                  <div className="input image-upload">
                    <Label attr="image" text="Image" errors={errors} />
                    <div className="image-upload-hint">
                      Image should be a .png or .jpg and have an aspect ratio of
                      15:13 (e.g. 750w x 650h)
                    </div>
                    <ImageUploader
                      id="backgroundImage"
                      onUploaded={this.handleImageUploaded}
                      source={this.state.imageUrl}
                      type="offer"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <h3>Availability</h3>
              <div className="availability">
                <div className="availability-field start-date">
                  <Label attr="startDate" text="Start Date" errors={errors} />
                  <DatePicker
                    id="startDate"
                    selected={DatePresenter.dateObject(this.state.startDate)}
                    onChange={(date) => this.handleStartDate(date)}
                    dateFormat="MM-dd-yyyy"
                  />
                </div>
                <div className="availability-field start-time">
                  <div className="">
                    <Label attr="startTime" text="Start Time" />
                    <select
                      id="startTime"
                      value={this.state.startTime}
                      onChange={this.handleStartTime}
                    >
                      {HOURS.map((hour, i) => (
                        <option value={i + 1} key={i}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="availability-field end-date">
                  <Label attr="endDate" text="End Date" errors={errors} />
                  <DatePicker
                    id="endDate"
                    selected={DatePresenter.dateObject(this.state.endDate)}
                    onChange={(date) => this.handleEndDate(date)}
                    dateFormat="MM-dd-yyyy"
                  />
                </div>
                <div className="availability-field end-time">
                  <div className="">
                    <Label attr="endTime" text="End Time" />
                    <select
                      id="endTime"
                      value={this.state.endTime}
                      onChange={this.handleEndTime}
                    >
                      {HOURS.map((hour, i) => (
                        <option value={i + 1} key={i}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <h3>Redemption</h3>
              <div className="form-group-fields">
                <div className="form-group-column">
                  <div className="redemption-code-field">
                    <Label
                      attr="redemptionCode"
                      text="Redemption Code"
                      errors={errors}
                    />
                    <input
                      id="redemptionCode"
                      value={this.state.redemptionCode}
                      onChange={this.handleRedemptionCode}
                    />
                  </div>

                  <div className="is-redeemable-field checkbox">
                    <input
                      type="checkbox"
                      id="isRedeemable"
                      checked={this.state.isRedeemable}
                      onChange={this.handleIsRedeemable}
                    />
                    <Label
                      attr="isRedeemable"
                      text="Is Redeemable"
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

const OfferNewContainer = withRouter(
  createFragmentContainer(OfferNewView, {
    lists: graphql`
      fragment OfferNew_lists on Lists {
        id
        stores(first: 1000) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    `,
  })
);

export default class OfferNew extends Component {
  render() {
    return (
      <StoreProvider
        modern
        container={OfferNewContainer}
        query={graphql`
          query OfferNewQuery {
            lists {
              ...OfferNew_lists
            }
          }
        `}
      />
    );
  }
}
